// # Calendar Day Dialog Component
// Full day overview dialog that opens when clicking on a day

'use client';

import React, { useState, useCallback } from 'react';
import { X, Plus, Clock } from 'lucide-react';
import type { CalendarEvent, CalendarEventFormData } from '../../types/calendar';
import { CalendarEventChip } from './CalendarEventChip';
import { CalendarEventForm } from './CalendarEventForm';
import { CalendarService } from '../../lib/calendar-service';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

interface CalendarDayDialogProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date | null;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onCreateEvent?: (date: Date, eventData?: any) => Promise<void>;
  departments?: Array<{ id: string; name: string }>;
}

export function CalendarDayDialog({
  isOpen,
  onClose,
  date,
  events,
  onEventClick,
  onCreateEvent,
  departments = []
}: CalendarDayDialogProps) {
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const { isMobile } = useDeviceInfo();

  // # Get events for the selected date
  const getEventsForDate = useCallback((): CalendarEvent[] => {
    if (!date || !events || events.length === 0) return [];
    
    const dateStr = formatDate(date);
    
    return events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      const normalizedStart = event.start_date.split('T')[0];
      const normalizedEnd = event.end_date.split('T')[0];
      return normalizedStart <= dateStr && normalizedEnd >= dateStr;
    }).sort((a, b) => {
      // Sort by time if available
      if (a.start_time && b.start_time) {
        return a.start_time.localeCompare(b.start_time);
      }
      if (a.start_time) return -1;
      if (b.start_time) return 1;
      return 0;
    });
  }, [date, events]);

  // # Format date helper
  const formatDate = useCallback((date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // # Handle event click
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventForm(true);
    onEventClick?.(event);
  };

  // # Handle add button click
  const handleAddClick = () => {
    if (!date) return;
    setSelectedEvent(null);
    setShowEventForm(true);
  };

  // # Handle form submit
  const handleFormSubmit = async (data: CalendarEventFormData) => {
    if (!date) return;
    
    try {
      if (selectedEvent) {
        // Update existing event
        const result = await CalendarService.updateEvent(selectedEvent.id, data);
        if (result.success) {
          setShowEventForm(false);
          setSelectedEvent(null);
          // Reload events by calling onCreateEvent to trigger refresh
          if (onCreateEvent) {
            await onCreateEvent(date);
          }
        }
      } else {
        // Create new event
        if (onCreateEvent) {
          await onCreateEvent(date, data);
        } else {
          const result = await CalendarService.createEvent(data);
          if (result.success) {
            setShowEventForm(false);
            setSelectedEvent(null);
          }
        }
      }
    } catch (error) {
      console.error('Error creating/updating event:', error);
    }
  };

  // # Handle form cancel
  const handleFormCancel = () => {
    setShowEventForm(false);
    setSelectedEvent(null);
  };

  // # Close on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showEventForm) {
          handleFormCancel();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showEventForm, onClose]);

  if (!isOpen || !date) return null;

  const dayEvents = getEventsForDate();
  const isToday = (() => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  })();

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className={`relative bg-white dark:bg-gray-900 shadow-2xl flex flex-col ${
          isMobile
            ? 'w-full h-full'
            : 'w-1/2 h-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex-shrink-0 border-b border-gray-200 dark:border-gray-700 ${
          isMobile ? 'p-3' : 'p-4'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`font-bold text-gray-900 dark:text-gray-100 ${
                isMobile ? 'text-lg' : 'text-xl'
              }`}>
                {date.toLocaleDateString('no-NO', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h2>
              {isToday && (
                <span className={`text-blue-600 dark:text-blue-400 font-medium ${
                  isMobile ? 'text-xs mt-1' : 'text-sm mt-1'
                }`}>
                  I dag
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className={`text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${
                isMobile ? 'p-2' : 'p-2.5'
              }`}
            >
              <X className={isMobile ? 'w-5 h-5' : 'w-6 h-6'} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {showEventForm ? (
            <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
              <CalendarEventForm
                event={selectedEvent || undefined}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
                departments={departments}
                defaultDate={formatDate(date)}
              />
            </div>
          ) : (
            <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
              {/* Add button */}
              <button
                onClick={handleAddClick}
                className={`w-full mb-6 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium ${
                  isMobile ? 'text-sm' : 'text-base'
                }`}
              >
                <Plus className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} />
                <span>Legg til</span>
              </button>

              {/* Events list */}
              {dayEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Clock className={`text-gray-400 ${isMobile ? 'w-12 h-12' : 'w-16 h-16'} mb-4`} />
                  <p className={`text-gray-500 ${isMobile ? 'text-sm' : 'text-base'}`}>
                    Ingen hendelser planlagt for denne dagen
                  </p>
                  <button
                    onClick={handleAddClick}
                    className={`mt-4 text-blue-600 hover:text-blue-700 font-medium ${
                      isMobile ? 'text-sm' : 'text-base'
                    }`}
                  >
                    Opprett en hendelse
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${
                    isMobile ? 'text-sm' : 'text-base'
                  } mb-4`}>
                    {dayEvents.length} hendelse{dayEvents.length !== 1 ? 'r' : ''}
                  </h3>
                  {dayEvents.map((event) => (
                    <CalendarEventChip
                      key={event.id}
                      event={event}
                      onClick={handleEventClick}
                      compact={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CalendarDayDialog;

